import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, ZoomControl, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css'
import TrafficLightMarkers from './markers/TrafficLightMarkers';
import LocateControl from "./controls/LocateControl";
import { TrafficInfra } from "../trafficInfra/TrafficInfra";
import AppTitle from "./controls/AppTitle";
import { TunnelState } from "../tunnelState/TunnelState";
import TunnelMarkers from "./markers/TunnelMarkers";
import RoutingMachine from "./logic/RoutingMachine";
import TrafficLightHUD from "../deviceState/TrafficLightHUD";
import { useSelector, useDispatch } from 'react-redux';

import ControlContainer from "./controls/ControlContainer";
import LanguageSwitcher from "./controls/LanguageSwitcher";
import NavigationActionButton from "./controls/NavigationActionButton";
import CompassControl from "./controls/CompassControl";
import DebugControls from "./debug/DebugControls";
import { useDebugMode } from "./debug/useDebugMode";
import "./logic/LeafletSetup";
import styles from './Map.module.css';
import versionStyles from './controls/VersionDisplay.module.css';
import { findActiveTrafficLight, findNearestPointOnRoute, calculateBearing, calculateManeuver, calculateDistance } from "./logic/NavigationHelpers";
import { useGPSTracking } from "./hooks/useGPSTracking";
import { useDynamicZoom } from "./hooks/useDynamicZoom";
import { useAutoMapOffset } from "./hooks/useAutoMapOffset";
import { useSmoothMapTransition } from "./hooks/useSmoothMapTransition";
import { UserMarker } from "./markers/UserMarker";
import {
    setRouteCoordinates,
    setIsRoutingMode,
    setIsDrivingMode,
    setActiveLight,
    setActiveLightDistance,
    setIsHudVisible,
    setWaypoints,
    setRouteProximity,
    setHudDistanceThreshold,
    setHudTimeThreshold,
    selectCurrentLocation,
    selectZoom,
    selectRouteCoordinates,
    selectIsRoutingMode,
    selectIsDrivingMode,
    selectCurrentBearing,
    selectCurrentSpeed,
    selectActiveLight,
    selectActiveLightDistance,
    selectIsHudVisible,
    selectWaypoints,
    selectRouteProximity,
    selectHudDistanceThreshold,
    selectHudTimeThreshold,
    setZoom,
    setCurrentBearing,
    setCurrentSpeed,
    setIntersectionState,
    selectIntersectionState,
    selectIsHeadingUp,
    setIsHeadingUp,
    selectCenterOffset,
    setCenterOffset,
} from './mapSlice';
import { selectIntersections, selectTopologies, fetchTopology } from "../trafficInfra/trafficInfraSlice";
import { matchSignal } from "./logic/SignalMatcher";


// Component to handle map clicks
const MapClickHandler = ({ onClick }) => {
    useMapEvents({
        click: (e) => {
            onClick(e.latlng);
        },
    });
    return null;
};

// Component to handle map cursor
const MapCursor = ({ isRoutingMode, waypoints }) => {
    const map = useMapEvents({});
    useEffect(() => {
        const container = map.getContainer();
        // Reset classes first
        container.classList.remove('cursor-start', 'cursor-end');

        if (isRoutingMode) {
            if (waypoints.length === 0) {
                container.classList.add('cursor-start');
            } else if (waypoints.length === 1) {
                container.classList.add('cursor-end');
            }
        }
    }, [map, isRoutingMode, waypoints]);
    return null;
};


// Helper to center map on user location updates
const MapCentering = ({ center, isDrivingMode, centerOffset, isHeadingUp, bearing }) => {
    const map = useMap();
    useEffect(() => {
        if (isDrivingMode && center) {
            if (centerOffset.x === 0 && centerOffset.y === 0) {
                map.setView(center, map.getZoom(), { animate: true });
            } else {
                // Determine the target center point in pixels
                const zoom = map.getZoom();
                const centerPoint = map.project(center, zoom);

                // Calculate rotated offset
                let offsetX = centerOffset.x;
                let offsetY = centerOffset.y;

                if (isHeadingUp) {
                    // Convert bearing to radians
                    const angle = (bearing * Math.PI) / 180;

                    // Rotate the offset vector (x, y) by the bearing to match map rotation
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);
                    offsetX = centerOffset.x * cos - centerOffset.y * sin;
                    offsetY = centerOffset.x * sin + centerOffset.y * cos;
                }

                // Adjust by offset (subtracting offset moves the map so the point is "offset" from center)
                const targetPoint = centerPoint.subtract([offsetX, offsetY]);
                const targetLatLng = map.unproject(targetPoint, zoom);

                map.setView(targetLatLng, zoom, { animate: true });
            }
        }
    }, [center, isDrivingMode, map, centerOffset, isHeadingUp, bearing]);
    return null;
};

// Helper to handle map rotation
const MapRotation = ({ isHeadingUp, bearing, isDrivingMode }) => {
    const map = useMap();
    const bearingRef = React.useRef(bearing);

    // Keep ref updated
    useEffect(() => {
        bearingRef.current = bearing;
    }, [bearing]);

    useEffect(() => {
        const mapPane = map.getPanes().mapPane;
        const applyRotation = () => {
            if (!isDrivingMode || !isHeadingUp || !mapPane) return;

            // Add transition for smooth rotation ONLY when enabled
            mapPane.style.transition = 'transform 0.3s ease-out';

            const currentBearing = bearingRef.current;
            const currentTransform = mapPane.style.transform;

            // Fix Pivot: Calculate the center of the viewport relative to the map pane layer
            const centerPoint = map.latLngToLayerPoint(map.getCenter());
            mapPane.style.transformOrigin = `${centerPoint.x}px ${centerPoint.y}px`;

            // If Leaflet has cleared the transform (rare) or updated it
            let newTransform = currentTransform;
            if (currentTransform.includes('rotate')) {
                newTransform = currentTransform.replace(/rotate\([^)]+\)/, `rotate(-${currentBearing}deg)`);
            } else {
                newTransform = `${currentTransform} rotate(-${currentBearing}deg)`;
            }

            // Only apply if changed to avoid thrashing
            if (mapPane.style.transform !== newTransform) {
                mapPane.style.transform = newTransform;
            }
        };

        const resetRotation = () => {
            if (!mapPane) return;
            // Clear custom origin
            mapPane.style.transformOrigin = '';
            mapPane.style.transition = '';

            const currentTransform = mapPane.style.transform;
            if (currentTransform.includes('rotate')) {
                mapPane.style.transform = currentTransform.replace(/rotate\([^)]+\)/, '');
            }
        };

        if (isDrivingMode && isHeadingUp) {
            // Apply immediately
            applyRotation();

            // Listen to move events (Leaflet fires these when dragging/panning/zooming)
            map.on('move', applyRotation);
            map.on('moveend', applyRotation);
            map.on('zoom', applyRotation);

            // Disable interactions
            map.dragging.disable();
            map.touchZoom.disable();
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
            if (map.tap) map.tap.disable();
        } else {
            resetRotation();

            // Re-enable interactions
            map.dragging.enable();
            map.touchZoom.enable();
            map.doubleClickZoom.enable();
            map.scrollWheelZoom.enable();
            map.boxZoom.enable();
            map.keyboard.enable();
            if (map.tap) map.tap.enable();
        }

        return () => {
            map.off('move', applyRotation);
            map.off('moveend', applyRotation);
            map.off('zoom', applyRotation);
        };
    }, [isHeadingUp, isDrivingMode, map]);

    // Trigger effect when bearing changes
    useEffect(() => {
        if (isDrivingMode && isHeadingUp) {
            const mapPane = map.getPanes().mapPane;
            if (mapPane) {
                const centerPoint = map.latLngToLayerPoint(map.getCenter());
                mapPane.style.transformOrigin = `${centerPoint.x}px ${centerPoint.y}px`;

                const currentTransform = mapPane.style.transform;
                if (currentTransform.includes('rotate')) {
                    mapPane.style.transform = currentTransform.replace(/rotate\([^)]+\)/, `rotate(-${bearing}deg)`);
                } else {
                    mapPane.style.transform = `${currentTransform} rotate(-${bearing}deg)`;
                }
            }
        }
    }, [bearing, isDrivingMode, isHeadingUp, map]);

    return null;
};

// Component to handle dynamic zooming inside the map context
const DynamicZoomController = () => {
    useDynamicZoom();
    return null;
};

const SmoothMapController = ({ center, bearing, isDrivingMode, isHeadingUp, centerOffset }) => {
    useSmoothMapTransition({
        targetCenter: center,
        targetBearing: bearing,
        isDrivingMode,
        isHeadingUp,
        centerOffset
    });
    return null;
};

import VersionDisplay from "./controls/VersionDisplay";

const MapView = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [debugEnabled, setDebugEnabled] = useDebugMode();
    const intersections = useSelector(selectIntersections);
    const topologies = useSelector(selectTopologies);

    const currentLocation = useSelector(selectCurrentLocation);
    const zoom = useSelector(selectZoom);
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const isRoutingMode = useSelector(selectIsRoutingMode);
    const isDrivingMode = useSelector(selectIsDrivingMode);
    const currentBearing = useSelector(selectCurrentBearing);
    const currentSpeed = useSelector(selectCurrentSpeed);
    const activeLight = useSelector(selectActiveLight);
    const activeLightDistance = useSelector(selectActiveLightDistance);
    const isHudVisible = useSelector(selectIsHudVisible);
    const waypoints = useSelector(selectWaypoints);
    const routeProximity = useSelector(selectRouteProximity);
    const hudDistanceThreshold = useSelector(selectHudDistanceThreshold);
    const hudTimeThreshold = useSelector(selectHudTimeThreshold);
    const intersectionState = useSelector(selectIntersectionState);
    const isHeadingUp = useSelector(selectIsHeadingUp);
    const centerOffset = useSelector(selectCenterOffset);

    // Filter intersections to only those on the route
    const relevantIntersections = React.useMemo(() => {
        if (!routeCoordinates || !intersections?.features) return intersections;

        const filteredFeatures = intersections.features.filter(feature => {
            const { geometry } = feature;
            if (!geometry) return false;
            const { coordinates } = geometry;
            // coordinates are [lng, lat] in GeoJSON
            const markerLatLng = L.latLng(coordinates[0][1], coordinates[0][0]);

            // Check if marker is close to any point on the route
            return routeCoordinates.some(coord => markerLatLng.distanceTo(coord) < routeProximity);
        });

        return { ...intersections, features: filteredFeatures };
    }, [routeCoordinates, intersections, routeProximity]);

    const checkProximity = (location, bearing) => {
        // Only auto-select lights in Driving Mode
        if (!isDrivingMode) return;

        // Use a large search radius to find the light even if far away (for debug info)
        const SEARCH_RADIUS = 2000;

        const { activeLight, distance } = findActiveTrafficLight(
            location,
            relevantIntersections,
            bearing,
            SEARCH_RADIUS,
            45 // Approach angle (degrees)
        );

        if (activeLight) {
            // New Topology Integration Check
            const topology = topologies[activeLight.liva_nro]; // e.g. "123"

            // If we have an active intersection but no topology, try to fetch it
            if (!topology && activeLight.liva_nro) {
                // Use yhteinen_koje as fallback (e.g. 536b -> 536) if primary topology is missing
                dispatch(fetchTopology(activeLight.liva_nro, activeLight.yhteinen_koje));
            }

            let matchedSignal = null;
            const hasRoute = routeCoordinates && routeCoordinates.length > 0;

            let intersectionStateUpdate = { entryHeading: null, exitHeading: null };

            if (hasRoute) {
                // Entry Bearing: Calculate from the ROUTE geometry.
                // Backtrack ~30 meters to get the stable approach vector
                const lightLoc = { lat: activeLight.lat, lng: activeLight.lng };
                const { index: lightIndex } = findNearestPointOnRoute(lightLoc, routeCoordinates);

                let approachPoint = null;
                const BEARING_LOOKAHEAD_DIST = 30; // meters

                for (let i = lightIndex; i >= 0; i--) {
                    const pt = routeCoordinates[i];
                    const dist = calculateDistance(lightLoc.lat, lightLoc.lng, pt.lat, pt.lng);
                    if (dist >= BEARING_LOOKAHEAD_DIST) {
                        approachPoint = pt;
                        break;
                    }
                }

                let entryBearing = null;

                if (approachPoint) {
                    entryBearing = calculateBearing(
                        approachPoint.lat,
                        approachPoint.lng,
                        lightLoc.lat,
                        lightLoc.lng
                    );
                } else {
                    // Fallback to Vehicle -> Light if at start of route or path too short
                    entryBearing = calculateBearing(
                        currentLocation.lat,
                        currentLocation.lng,
                        activeLight.lat,
                        activeLight.lng
                    );
                }

                // Exit Bearing: Light -> Next Point on Route (after light)
                // Look ahead ~30 meters to capture the turn (exit vector)
                let exitBearing = entryBearing; // Default to straight
                let exitPoint = null;

                for (let i = lightIndex; i < routeCoordinates.length; i++) {
                    const pt = routeCoordinates[i];
                    const dist = calculateDistance(lightLoc.lat, lightLoc.lng, pt.lat, pt.lng);
                    if (dist >= BEARING_LOOKAHEAD_DIST) {
                        exitPoint = pt;
                        break;
                    }
                }
                // If route ends before lookahead, use last point
                if (!exitPoint && routeCoordinates.length > 0) {
                    exitPoint = routeCoordinates[routeCoordinates.length - 1];
                }

                if (exitPoint) {
                    exitBearing = calculateBearing(
                        lightLoc.lat,
                        lightLoc.lng,
                        exitPoint.lat,
                        exitPoint.lng
                    );
                }

                intersectionStateUpdate = {
                    entryHeading: entryBearing,
                    exitHeading: exitBearing
                };

                // Use NavigationHelpers.calculateManeuver
                const maneuver = calculateManeuver(entryBearing, exitBearing);

                // Try Signal Matching if Topology available
                if (topology && !topology.status) {
                    // We match based on ROUTE approach bearing (entryBearing) + Maneuver
                    try {
                        // Pass entryBearing (stable) instead of bearing (gps)
                        matchedSignal = matchSignal(topology, entryBearing, maneuver);
                    } catch (err) {
                        console.error("Signal matching failed:", err);
                    }
                }
            } else {
                const entryBearing = calculateBearing(
                    currentLocation.lat,
                    currentLocation.lng,
                    activeLight.lat,
                    activeLight.lng
                );
                intersectionStateUpdate = {
                    entryHeading: entryBearing,
                    exitHeading: null
                };
            }

            const enhancedActiveLight = {
                ...activeLight,
                matchedSignal: matchedSignal
            };

            dispatch(setActiveLight(enhancedActiveLight));
            dispatch(setIntersectionState(intersectionStateUpdate));


        } else {
            dispatch(setActiveLight(null));
            dispatch(setIntersectionState({ entryHeading: null, exitHeading: null }));
        }
        dispatch(setActiveLightDistance(distance));

        // Determine if HUD should be visible
        if (activeLight) {
            const timeToLight = currentSpeed > 0 ? distance / (currentSpeed / 3.6) : Infinity;
            const shouldShow = distance <= hudDistanceThreshold || timeToLight <= hudTimeThreshold;
            dispatch(setIsHudVisible(shouldShow));
        } else {
            dispatch(setIsHudVisible(false));
        }
    };

    const handleRouteFound = useCallback((coordinates) => {
        dispatch(setRouteCoordinates(coordinates));
    }, [dispatch]);

    const toggleRoutingMode = () => {
        const enteringRouting = !isRoutingMode;
        dispatch(setIsRoutingMode(enteringRouting));
        if (enteringRouting && !debugEnabled && currentLocation) {
            // Auto-set start point to current GPS location in non-debug mode
            dispatch(setWaypoints([{ lat: currentLocation.lat, lng: currentLocation.lng }]));
        } else {
            dispatch(setWaypoints([]));
        }
        dispatch(setRouteCoordinates(null));
    };

    // Use GPS tracking for navigation (can be mocked via console monkey patching)
    useGPSTracking(checkProximity);
    useAutoMapOffset();

    const toggleDrivingMode = () => {
        dispatch(setIsDrivingMode(!isDrivingMode));
    };

    const handleMapClick = (latlng) => {
        if (!isRoutingMode) return;

        if (waypoints.length < 2) {
            dispatch(setWaypoints([...waypoints, { lat: latlng.lat, lng: latlng.lng }]));
        }
    };

    const handleProximityChange = (e) => {
        dispatch(setRouteProximity(parseInt(e.target.value, 10)));
    };

    const handleHudDistanceChange = (e) => {
        dispatch(setHudDistanceThreshold(parseInt(e.target.value, 10)));
    };

    const handleHudTimeChange = (e) => {
        dispatch(setHudTimeThreshold(parseInt(e.target.value, 10)));
    };


    const locateOptions = {
        position: 'topright',
        strings: {
            title: t('map.locate_me')
        },
        locateOptions: {
            enableHighAccuracy: true
        },
    };

    return (
        <MapContainer center={currentLocation} zoom={zoom} zoomControl={false}>
            <DynamicZoomController />
            <MapCursor isRoutingMode={isRoutingMode} waypoints={waypoints} />

            {/* Use Manual Animation Loop for Heading Up Driving, else standard Leaflet */}
            {isDrivingMode && isHeadingUp ? (
                <SmoothMapController
                    center={currentLocation}
                    bearing={currentBearing}
                    isDrivingMode={isDrivingMode}
                    isHeadingUp={isHeadingUp}
                    centerOffset={centerOffset}
                />
            ) : (
                <>
                    <MapCentering center={currentLocation} isDrivingMode={isDrivingMode} centerOffset={centerOffset} isHeadingUp={isHeadingUp} bearing={currentBearing} />
                    <MapRotation isHeadingUp={isHeadingUp} bearing={currentBearing} isDrivingMode={isDrivingMode} />
                </>
            )}

            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
                keepBuffer={8}
                updateWhenZooming={false}
                updateWhenIdle={false}
            />
            <MapClickHandler onClick={handleMapClick} />

            <TrafficLightMarkers routeCoordinates={routeCoordinates} routeProximity={routeProximity} />
            <TunnelMarkers />

            {isDrivingMode && (
                <UserMarker
                    position={currentLocation}
                    bearing={currentBearing}
                />
            )}

            {isDrivingMode && activeLight && isHudVisible && (
                <TrafficLightHUD activeLight={activeLight} distance={activeLightDistance} />
            )}

            <RoutingMachine
                onRouteFound={handleRouteFound}
                waypoints={isRoutingMode ? waypoints : []}
                isDrivingMode={isDrivingMode}
            />

            <ZoomControl
                zoomInTitle={t('map.zoom_in')}
                zoomOutTitle={t('map.zoom_out')}
                position={"topright"} />

            <ControlContainer position={'topleft'}>
                {!isDrivingMode && <AppTitle isRoutingMode={isRoutingMode} />}
                {!isDrivingMode && <TrafficInfra />}
                {!isDrivingMode && <TunnelState />}

                <div className={styles.TopLeftControlStack}>
                    <NavigationActionButton
                        isRoutingMode={isRoutingMode}
                        isDrivingMode={isDrivingMode}
                        hasRoute={!!routeCoordinates}
                        onToggleRouting={toggleRoutingMode}
                        onToggleDriving={toggleDrivingMode}
                    />


                    {/* Debug controls - shown when debug mode is enabled (type "nysse" to toggle) */}
                    <DebugControls
                        debugEnabled={debugEnabled}
                        setDebugEnabled={setDebugEnabled}
                        routeProximity={routeProximity}
                        onProximityChange={handleProximityChange}
                        hudDistanceThreshold={hudDistanceThreshold}
                        onHudDistanceChange={handleHudDistanceChange}
                        hudTimeThreshold={hudTimeThreshold}
                        onHudTimeChange={handleHudTimeChange}
                        isDrivingMode={isDrivingMode}
                        isRoutingMode={isRoutingMode}
                        currentBearing={currentBearing}
                        currentSpeed={currentSpeed}
                        activeLightDistance={activeLightDistance}
                        setIsHeadingUp={setIsHeadingUp}
                        onBearingChange={(e) => dispatch(setCurrentBearing(parseInt(e.target.value, 10)))}
                        onSpeedChange={(e) => dispatch(setCurrentSpeed(parseInt(e.target.value, 10)))}
                        intersectionState={intersectionState}
                        isHudVisible={isHudVisible}
                    />
                </div>
            </ControlContainer>

            {/* Top Right Controls Stack */}
            <ControlContainer position={'topright'}>
            </ControlContainer>

            {/* LocateControl usually mounts itself. */}
            {!isDrivingMode && <LocateControl options={locateOptions} />}

            {/* Language Switcher */}
            {!isDrivingMode && (
                <ControlContainer position={'topright'}>
                    <div className="leaflet-bar">
                        <LanguageSwitcher />
                    </div>
                </ControlContainer>
            )}

            {/* Compass Control - positioned with other top-right controls */}
            <ControlContainer position={'topright'} className="leaflet-bar">
                <CompassControl
                    bearing={currentBearing}
                    isDrivingMode={isDrivingMode}
                />
            </ControlContainer>

            <ControlContainer position={'bottomleft'} className={versionStyles.Container}>
                <VersionDisplay />
            </ControlContainer>
        </MapContainer>
    );
};

export default MapView;
